import React, { useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { AlertTriangle, Shield, Calendar, User, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Clock, MapPin, Users, Bell, Play, Timer, Layers, Eye, ExternalLink } from 'lucide-react';
import { Button } from '../ui/button';
import { APP_VERSION_LABEL, APP_VERSION_DATE, APP_VERSION_NAME, formatFrenchDate } from '../../version';

const DashboardTab = ({ stats, eventTypes, t, freedivers = [], trainingSessions = [], language = 'en', activeLiveSession = null, onGoToLiveEvent, eventSessions = [] }) => {
  // Translation helper with fallback
  const translate = (key, fallback) => t ? t(key, fallback) : fallback;
  
  const [certWidgetExpanded, setCertWidgetExpanded] = useState(false);
  const [calendarWeekOffset, setCalendarWeekOffset] = useState(0);
  const [selectedCalendarDate, setSelectedCalendarDate] = useState(null);
  const [liveElapsed, setLiveElapsed] = useState(0);

  // Upcoming event sessions reminder
  const upcomingReminders = useMemo(() => {
    const now = Date.now();
    return eventSessions
      .filter(s => s.status === 'preparation' && s.start_time)
      .map(s => {
        const start = new Date(s.start_time).getTime();
        const minutesUntil = Math.floor((start - now) / 60000);
        return { ...s, minutesUntil };
      })
      .filter(s => s.minutesUntil > -5 && s.minutesUntil <= 60)
      .sort((a, b) => a.minutesUntil - b.minutesUntil);
  }, [eventSessions]);

  // Live event elapsed timer
  React.useEffect(() => {
    if (!activeLiveSession?.actual_start_time) {
      setLiveElapsed(0);
      return;
    }
    const start = new Date(activeLiveSession.actual_start_time).getTime();
    const tick = () => setLiveElapsed(Math.max(0, Math.floor((Date.now() - start) / 1000)));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [activeLiveSession?.actual_start_time]);

  // Calculate freedivers with expiring medical certificates (within 60 days - extended from 30)
  const expiringCertificates = useMemo(() => {
    const today = new Date();
    const sixtyDaysFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
    
    return freedivers
      .filter(freediver => {
        if (!freediver.medical_certificate_valid) {
          return false;
        }
        // Use expiry date if available, otherwise calculate from upload date
        let expirationDate;
        if (freediver.medical_certificate_expiry_date) {
          expirationDate = new Date(freediver.medical_certificate_expiry_date);
        } else if (freediver.medical_certificate_upload_date) {
          const uploadDate = new Date(freediver.medical_certificate_upload_date);
          expirationDate = new Date(uploadDate.getTime() + 365 * 24 * 60 * 60 * 1000);
        } else {
          return false;
        }
        // Include if expiring within 60 days or already expired
        return expirationDate <= sixtyDaysFromNow;
      })
      .map(freediver => {
        let expirationDate;
        if (freediver.medical_certificate_expiry_date) {
          expirationDate = new Date(freediver.medical_certificate_expiry_date);
        } else {
          const uploadDate = new Date(freediver.medical_certificate_upload_date);
          expirationDate = new Date(uploadDate.getTime() + 365 * 24 * 60 * 60 * 1000);
        }
        const today = new Date();
        const daysUntilExpiry = Math.ceil((expirationDate - today) / (24 * 60 * 60 * 1000));
        return {
          ...freediver,
          expirationDate,
          daysUntilExpiry,
          isExpired: daysUntilExpiry <= 0,
          urgency: daysUntilExpiry <= 0 ? 'expired' : daysUntilExpiry <= 7 ? 'critical' : daysUntilExpiry <= 30 ? 'warning' : 'notice'
        };
      })
      .sort((a, b) => a.daysUntilExpiry - b.daysUntilExpiry);
  }, [freedivers]);

  // Calendar week calculations
  const getWeekDates = (offset = 0) => {
    const today = new Date();
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - today.getDay() + 1 + (offset * 7)); // Start from Monday
    
    const weekDates = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(startOfWeek);
      date.setDate(startOfWeek.getDate() + i);
      weekDates.push(date);
    }
    return weekDates;
  };

  const weekDates = useMemo(() => getWeekDates(calendarWeekOffset), [calendarWeekOffset]);

  // Get sessions for a specific date
  const getSessionsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return trainingSessions.filter(session => {
      if (!session.training_date || session.is_template) return false;
      const sessionDate = session.training_date.split('T')[0];
      return sessionDate === dateStr;
    });
  };

  // Format date for display
  const formatWeekDay = (date) => {
    const dayNames = language === 'fr' 
      ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']
      : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return dayNames[date.getDay()];
  };

  const formatMonthYear = (dates) => {
    const firstDate = dates[0];
    const lastDate = dates[6];
    const options = { month: 'long', year: 'numeric' };
    const locale = language === 'fr' ? 'fr-FR' : 'en-US';
    
    if (firstDate.getMonth() === lastDate.getMonth()) {
      return firstDate.toLocaleDateString(locale, options);
    } else {
      return `${firstDate.toLocaleDateString(locale, { month: 'short' })} - ${lastDate.toLocaleDateString(locale, options)}`;
    }
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  // Calculate upcoming sessions (next 7 days) for reminders
  const upcomingSessions = useMemo(() => {
    const now = new Date();
    const sevenDaysFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    return trainingSessions
      .filter(session => {
        if (!session.training_date || session.is_template) return false;
        const sessionDate = new Date(session.training_date);
        return sessionDate >= now && sessionDate <= sevenDaysFromNow;
      })
      .map(session => {
        const sessionDate = new Date(session.training_date);
        const daysUntil = Math.ceil((sessionDate - now) / (24 * 60 * 60 * 1000));
        return {
          ...session,
          sessionDate,
          daysUntil,
          isToday: daysUntil === 0,
          isTomorrow: daysUntil === 1
        };
      })
      .sort((a, b) => a.sessionDate - b.sessionDate);
  }, [trainingSessions]);

  // Calculate capacity status for sessions
  const getCapacityStatus = (session) => {
    const currentParticipants = session.participants?.length || 0;
    const maxParticipants = session.pool_specs?.max_participants || 0;
    
    if (maxParticipants === 0) return { status: 'unknown', percentage: 0, label: '-' };
    
    const percentage = Math.round((currentParticipants / maxParticipants) * 100);
    
    if (currentParticipants >= maxParticipants) {
      return { status: 'full', percentage: 100, label: translate('full', 'Full'), color: 'red' };
    } else if (percentage >= 80) {
      return { status: 'nearFull', percentage, label: translate('nearFull', 'Near Full'), color: 'orange' };
    } else if (percentage >= 50) {
      return { status: 'filling', percentage, label: translate('filling', 'Filling'), color: 'yellow' };
    } else {
      return { status: 'available', percentage, label: translate('available', 'Available'), color: 'green' };
    }
  };

  // Sessions at capacity or near capacity for alerts
  const capacityAlerts = useMemo(() => {
    const now = new Date();
    
    return trainingSessions
      .filter(session => {
        if (session.is_template) return false;
        const sessionDate = new Date(session.training_date);
        // Only show alerts for future sessions or today's sessions
        if (sessionDate < now && sessionDate.toDateString() !== now.toDateString()) return false;
        
        const maxParticipants = session.pool_specs?.max_participants || 0;
        const currentParticipants = session.participants?.length || 0;
        if (maxParticipants === 0) return false;
        
        const percentage = (currentParticipants / maxParticipants) * 100;
        return percentage >= 80; // Show sessions at 80%+ capacity
      })
      .map(session => {
        const capacityStatus = getCapacityStatus(session);
        const sessionDate = new Date(session.training_date);
        const daysUntil = Math.ceil((sessionDate - now) / (24 * 60 * 60 * 1000));
        return {
          ...session,
          sessionDate,
          daysUntil,
          isToday: daysUntil === 0,
          isTomorrow: daysUntil === 1,
          ...capacityStatus
        };
      })
      .sort((a, b) => {
        // Sort by: full first, then by date
        if (a.status === 'full' && b.status !== 'full') return -1;
        if (a.status !== 'full' && b.status === 'full') return 1;
        return a.sessionDate - b.sessionDate;
      });
  }, [trainingSessions]);

  // Define images for each event type
  const eventImages = {
    DYN: "https://images.unsplash.com/photo-1462947760324-15811216b688?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHx1bmRlcndhdGVyJTIwc3dpbW1pbmclMjBmaW5zfGVufDB8fHx8MTc1NTA5MzE1OXww&ixlib=rb-4.1.0&q=85",
    STA: "https://images.unsplash.com/photo-1529782425869-29acf5f9cb1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxicmVhdGglMjBob2xkaW5nfGVufDB8fHx8MTc1NTA5MzE2N3ww&ixlib=rb-4.1.0&q=85",
    DNF: "https://images.unsplash.com/photo-1603259860985-5dfe6bf9a1fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwzfHxmcmVlZGl2aW5nfGVufDB8fHx8MTc1NTA5MzE3M3ww&ixlib=rb-4.1.0&q=85",
    DYNBF: "https://images.unsplash.com/photo-1674786375512-0b1d38fda375?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxmcmVlZGl2aW5nfGVufDB8fHx8MTc1NTA5MzE3M3ww&ixlib=rb-4.1.0&q=85",
    CNF: "https://images.unsplash.com/photo-1743656619958-68d85790bdb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxmcmVlZGl2aW5nJTIwZGVwdGh8ZW58MHx8fHwxNzU1MTA1MDY2fDA&ixlib=rb-4.1.0&q=85",
    CWT: "https://images.unsplash.com/photo-1743656620385-5afe20b69b9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHxmcmVlZGl2aW5nJTIwZGVwdGh8ZW58MHx8fHwxNzU1MTA1MDY2fDA&ixlib=rb-4.1.0&q=85",
    CWTBF: "https://images.pexels.com/photos/33336514/pexels-photo-33336514.jpeg",
    FIM: "https://images.pexels.com/photos/5256400/pexels-photo-5256400.jpeg"
  };

  // Define detailed descriptions for each event type
  const getDetailedDescription = (code, eventType) => {
    // If translation function exists, try to get translated descriptions
    if (translate) {
      const descKey = `desc${code}`;
      const translatedDesc = translate(descKey, null);
      if (translatedDesc && translatedDesc !== descKey) {
        return translatedDesc;
      }
    }
    
    const descriptions = {
      DYN: "The freediver swims underwater horizontally in a pool, covering the maximum distance possible with monofin or bi-fins. This is a pool discipline requiring excellent streamlining and efficient fin technique.",
      STA: "The freediver floats motionless at the surface, face down in the water, holding their breath for the maximum time possible. Mental preparation and relaxation techniques are crucial for this discipline.",
      DNF: "Swimming underwater horizontally without any fins, relying purely on arm and leg swimming technique. This discipline requires exceptional swimming skills and underwater efficiency.",
      DYNBF: "Similar to Dynamic Apnea but performed specifically with bi-fins (two separate fins). This classic technique allows for powerful propulsion through alternating fin kicks.",
      CNF: "A depth discipline where the freediver descends and ascends without fins and without pulling on the rope. Pure swimming technique and perfect buoyancy control are essential.",
      CWT: "The most popular depth discipline where freedivers use a monofin or bi-fins to reach maximum depth. Athletes must maintain the same weight throughout the dive.",
      CWTBF: "A depth discipline performed with bi-fins specifically, allowing for the classic freediving technique. Popular among athletes who prefer the stability of two fins.",
      FIM: "Freedivers descend by pulling themselves down a rope without using fins. This discipline relies on arm strength, technique, and efficient movement along the guide rope."
    };
    return descriptions[code] || eventType.description;
  };

  // Define correct scoring rules
  const getScoringRule = (code) => {
    if (code === 'STA') {
      return translate('scoringPointsPer5Seconds', '1 point per 5 seconds');
    } else {
      return translate('scoringPointsPerMeter', '0.5 point per meter');
    }
  };

  // Define discipline categories for badges with translations
  const getDisciplineType = (code) => {
    const poolDisciplines = ['DYN', 'STA', 'DNF', 'DYNBF'];
    const depthDisciplines = ['CNF', 'CWT', 'CWTBF', 'FIM'];
    
    if (poolDisciplines.includes(code)) {
      return { type: translate('disciplinePool', 'Pool'), color: 'bg-blue-700/80 text-blue-200 border-blue-600/50' };
    } else if (depthDisciplines.includes(code)) {
      return { type: translate('disciplineDepth', 'Depth'), color: 'bg-indigo-700/80 text-indigo-200 border-indigo-600/50' };
    }
    return { type: translate('disciplineEvent', 'Event'), color: 'bg-gray-700/80 text-gray-200 border-gray-600/50' };
  };

  // Get translated event name
  const getEventName = (code, defaultName) => {
    const eventNameKeys = {
      STA: 'staticApnea',
      DYN: 'dynamicWithFins',
      DNF: 'dynamicNoFins',
      DYNBF: 'dynamicBifins',
      CWT: 'constantWeight',
      CNF: 'constantWeightNoFins',
      CWTBF: 'constantWeightBifins',
      FIM: 'freeImmersion'
    };
    return translate(eventNameKeys[code], defaultName);
  };

  return (
    <div className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-4xl font-bold mb-4 bg-gradient-to-r from-blue-200 to-teal-200 bg-clip-text text-transparent">
          {translate('dashboardTitle', 'May the Freedive, Be With You!')}
        </h2>
        <p className="text-blue-300 text-lg">{translate('dashboardSubtitle', 'Manage your freediving competitions with precision and style')}</p>
      </div>

      {/* Event Approach Reminders */}
      {upcomingReminders.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="event-reminders">
          {upcomingReminders.map(session => (
            <div key={session.id} className={`flex items-center justify-between rounded-lg px-5 py-3 border ${
              session.minutesUntil <= 5 ? 'bg-red-900/40 border-red-500/60 animate-pulse' :
              session.minutesUntil <= 15 ? 'bg-orange-900/40 border-orange-500/50' :
              'bg-yellow-900/30 border-yellow-600/40'
            }`} data-testid={`event-reminder-${session.id}`}>
              <div className="flex items-center gap-3">
                <Timer className={`w-5 h-5 ${session.minutesUntil <= 5 ? 'text-red-400' : session.minutesUntil <= 15 ? 'text-orange-400' : 'text-yellow-400'}`} />
                <div>
                  <p className="text-white font-semibold text-sm">{session.name}</p>
                  <p className="text-gray-400 text-xs">
                    {session.minutesUntil <= 0 
                      ? translate('eventStartingNow', 'Starting now!')
                      : `${translate('startsIn', 'Starts in')} ${session.minutesUntil} min`
                    }
                  </p>
                </div>
              </div>
              <Badge className={session.minutesUntil <= 5 ? 'bg-red-600' : session.minutesUntil <= 15 ? 'bg-orange-600' : 'bg-yellow-600'}>
                {session.minutesUntil <= 0 ? 'NOW' : `${session.minutesUntil}m`}
              </Badge>
            </div>
          ))}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="bg-gradient-to-br from-blue-800/50 to-blue-900/50 border-blue-700/30 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-blue-200 text-sm font-medium">{translate('totalCompetitions', 'Total Competitions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.total_competitions || 0}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-teal-800/50 to-teal-900/50 border-teal-700/30 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-teal-200 text-sm font-medium">{translate('registeredFreedivers', 'Registered Freedivers')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.total_freedivers || 0}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-blue-700/50 to-teal-800/50 border-blue-600/30 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-blue-200 text-sm font-medium">{translate('eventRegistrations', 'Event Registrations')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.total_registrations || 0}</div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-800/50 to-teal-900/50 border-green-700/30 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-green-200 text-sm font-medium">{translate('activeCompetitions', 'Active Competitions')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-white">{stats.active_competitions || 0}</div>
          </CardContent>
        </Card>
      </div>

      {/* Live Event Widget */}
      {activeLiveSession && (
        <Card className="bg-gradient-to-br from-green-900/50 to-teal-900/50 border-green-600/40 backdrop-blur-sm relative overflow-hidden" data-testid="dashboard-live-event-widget">
          <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/10 rounded-full -mr-12 -mt-12 blur-2xl" />
          <CardHeader className="pb-2">
            <CardTitle className="text-green-200 flex items-center gap-2 text-base">
              <Badge className="bg-green-600 animate-pulse text-white text-xs">
                <Play size={10} className="mr-1" />
                LIVE
              </Badge>
              <span>{translate('liveEventActive', 'Live Event in Progress')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3">
              <div className="space-y-1.5">
                <p className="text-white font-semibold text-lg leading-tight">
                  {activeLiveSession.competition_name}
                </p>
                <p className="text-teal-300 text-sm">
                  {activeLiveSession.name}
                  {activeLiveSession.event_type_code && (
                    <span className="text-teal-400 ml-2">({activeLiveSession.event_type_code})</span>
                  )}
                </p>
                <div className="flex items-center gap-4 text-xs text-green-300 flex-wrap">
                  {activeLiveSession.location && (
                    <span className="flex items-center gap-1">
                      <MapPin size={12} className="text-teal-400" />
                      {activeLiveSession.location}
                    </span>
                  )}
                  {activeLiveSession.pool_type && (
                    <span className="flex items-center gap-1">
                      <Layers size={12} className="text-teal-400" />
                      {activeLiveSession.pool_type}{activeLiveSession.pool_length ? ` ${activeLiveSession.pool_length}m` : ''}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-green-400 text-xs mb-1">{translate('elapsed', 'Elapsed')}</p>
                  <div className="bg-green-800/40 rounded-lg px-4 py-2 border border-green-600/30">
                    <span className="text-2xl font-bold font-mono text-white" data-testid="dashboard-live-elapsed">
                      {String(Math.floor(liveElapsed / 3600)).padStart(2, '0')}:
                      {String(Math.floor((liveElapsed % 3600) / 60)).padStart(2, '0')}:
                      {String(liveElapsed % 60).padStart(2, '0')}
                    </span>
                  </div>
                </div>
                <Button
                  onClick={onGoToLiveEvent}
                  className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white"
                  size="sm"
                  data-testid="dashboard-go-to-live-btn"
                >
                  <Eye size={14} className="mr-1.5" />
                  {translate('viewLiveEvent', 'View Live Event')}
                </Button>
                <Button
                  onClick={() => window.open(`/spectator/${activeLiveSession.id}`, '_blank')}
                  className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                  size="sm"
                  data-testid="dashboard-spectator-btn"
                >
                  <ExternalLink size={14} className="mr-1.5" />
                  {translate('spectatorView', 'Spectator View')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expiring Medical Certificates Widget - Collapsible with 60-day warning */}
      {expiringCertificates.length > 0 && (
        <Card className="bg-gradient-to-br from-orange-900/40 to-red-900/40 border-orange-700/30 backdrop-blur-sm">
          <CardHeader 
            className="pb-3 cursor-pointer select-none hover:bg-orange-800/20 transition-colors rounded-t-lg"
            onClick={() => setCertWidgetExpanded(!certWidgetExpanded)}
            data-testid="cert-widget-toggle"
          >
            <CardTitle className="text-orange-200 flex items-center text-lg">
              <AlertTriangle className="mr-2 text-orange-400" size={20} />
              {translate('expiringCertificates', 'Expiring Medical Certificates')}
              <Badge className="ml-3 bg-orange-600 text-white">{expiringCertificates.length}</Badge>
              {expiringCertificates.some(c => c.urgency === 'expired') && (
                <Badge className="ml-2 bg-red-600 text-white animate-pulse">
                  {expiringCertificates.filter(c => c.urgency === 'expired').length} {translate('expired', 'Expired')}
                </Badge>
              )}
              <span className="ml-auto">
                {certWidgetExpanded ? <ChevronUp size={20} className="text-orange-400" /> : <ChevronDown size={20} className="text-orange-400" />}
              </span>
            </CardTitle>
          </CardHeader>
          {certWidgetExpanded && (
            <CardContent>
              <p className="text-orange-300 text-sm mb-4">
                {translate('expiringCertificatesDesc60', 'The following freedivers have medical certificates expiring within 60 days or already expired:')}
              </p>
              
              {/* Urgency Legend */}
              <div className="flex flex-wrap gap-3 mb-4 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-red-600"></div>
                  <span className="text-red-300">{translate('expired', 'Expired')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-orange-600"></div>
                  <span className="text-orange-300">&lt; 7 {translate('days', 'days')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-yellow-600"></div>
                  <span className="text-yellow-300">&lt; 30 {translate('days', 'days')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 rounded-full bg-blue-600"></div>
                  <span className="text-blue-300">&lt; 60 {translate('days', 'days')}</span>
                </div>
              </div>
              
              <div className="space-y-2 max-h-64 overflow-y-auto">
              {expiringCertificates.map((freediver) => (
                <div 
                  key={freediver.id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    freediver.urgency === 'expired'
                      ? 'bg-red-900/40 border border-red-600/50' 
                      : freediver.urgency === 'critical'
                        ? 'bg-orange-900/40 border border-orange-600/50'
                        : freediver.urgency === 'warning'
                          ? 'bg-yellow-900/30 border border-yellow-600/40'
                          : 'bg-blue-900/30 border border-blue-600/40'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {freediver.photo_url ? (
                      <img src={freediver.photo_url} alt="" className="w-10 h-10 rounded-full object-cover border border-orange-500/50" />
                    ) : (
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold ${
                        freediver.urgency === 'expired' ? 'bg-gradient-to-br from-red-600 to-red-800' :
                        freediver.urgency === 'critical' ? 'bg-gradient-to-br from-orange-600 to-red-600' :
                        freediver.urgency === 'warning' ? 'bg-gradient-to-br from-yellow-600 to-orange-600' :
                        'bg-gradient-to-br from-blue-600 to-teal-600'
                      }`}>
                        {freediver.first_name?.[0]}{freediver.last_name?.[0]}
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{freediver.first_name} {freediver.last_name}</p>
                      <p className="text-orange-300 text-xs flex items-center">
                        <Calendar size={12} className="mr-1" />
                        {translate('expires', 'Expires')}: {freediver.expirationDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US')}
                      </p>
                    </div>
                  </div>
                  <Badge className={`${
                    freediver.urgency === 'expired'
                      ? 'bg-red-600 text-white' 
                      : freediver.urgency === 'critical'
                        ? 'bg-orange-600 text-white'
                        : freediver.urgency === 'warning'
                          ? 'bg-yellow-600 text-white'
                          : 'bg-blue-600 text-white'
                  }`}>
                    {freediver.isExpired 
                      ? translate('expired', 'Expired')
                      : `${freediver.daysUntilExpiry} ${translate('daysLeft', 'days left')}`
                    }
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
          )}
        </Card>
      )}

      {/* Weekly Training Sessions Calendar */}
      <Card className="bg-gradient-to-br from-teal-900/40 to-blue-900/40 border-teal-700/30 backdrop-blur-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-teal-200 flex items-center text-lg">
              <Calendar className="mr-2 text-teal-400" size={20} />
              {translate('weeklyCalendar', 'Weekly Training Calendar')}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCalendarWeekOffset(calendarWeekOffset - 1)}
                className="text-teal-300 hover:text-white hover:bg-teal-700/50 h-8 w-8 p-0"
              >
                <ChevronLeft size={18} />
              </Button>
              <span className="text-teal-200 text-sm font-medium min-w-[160px] text-center">
                {formatMonthYear(weekDates)}
              </span>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setCalendarWeekOffset(calendarWeekOffset + 1)}
                className="text-teal-300 hover:text-white hover:bg-teal-700/50 h-8 w-8 p-0"
              >
                <ChevronRight size={18} />
              </Button>
              {calendarWeekOffset !== 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCalendarWeekOffset(0)}
                  className="text-teal-300 border-teal-600 hover:bg-teal-700/50 text-xs h-7 px-2"
                >
                  {translate('today', 'Today')}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-7 gap-2">
            {/* Day Headers */}
            {weekDates.map((date, idx) => (
              <div 
                key={`header-${idx}`}
                className={`text-center p-2 rounded-t-lg ${
                  isToday(date) 
                    ? 'bg-teal-600/50 text-white' 
                    : 'bg-slate-800/50 text-slate-400'
                }`}
              >
                <div className="text-xs font-medium">{formatWeekDay(date)}</div>
                <div className={`text-lg font-bold ${isToday(date) ? 'text-white' : 'text-slate-200'}`}>
                  {date.getDate()}
                </div>
              </div>
            ))}
            
            {/* Day Content */}
            {weekDates.map((date, idx) => {
              const sessions = getSessionsForDate(date);
              const hasToday = isToday(date);
              const isSelected = selectedCalendarDate && date.toDateString() === selectedCalendarDate.toDateString();
              const hasSessions = sessions.length > 0;
              
              return (
                <div 
                  key={`content-${idx}`}
                  onClick={() => hasSessions && setSelectedCalendarDate(isSelected ? null : date)}
                  className={`min-h-[100px] p-2 rounded-b-lg border transition-all ${
                    isSelected
                      ? 'bg-indigo-900/50 border-indigo-500 ring-2 ring-indigo-500/50 cursor-pointer'
                      : hasToday 
                        ? 'bg-teal-900/30 border-teal-600/50' 
                        : hasSessions
                          ? 'bg-slate-800/30 border-slate-700/30 hover:border-indigo-500/50 cursor-pointer'
                          : 'bg-slate-800/30 border-slate-700/30'
                  }`}
                >
                  {sessions.length === 0 ? (
                    <div className="text-slate-500 text-xs text-center mt-4">
                      {translate('noSessions', 'No sessions')}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {sessions.slice(0, 3).map((session, sessionIdx) => (
                        <div 
                          key={session.id || sessionIdx}
                          className="p-1.5 rounded bg-gradient-to-r from-teal-700/50 to-blue-700/50 border border-teal-600/30 text-xs"
                        >
                          <div className="text-teal-200 font-medium truncate">
                            {session.name || translate('trainingSession', 'Training Session')}
                          </div>
                          {session.start_time && (
                            <div className="text-teal-400 flex items-center gap-1 mt-0.5">
                              <Clock size={10} />
                              {session.start_time}
                            </div>
                          )}
                          {session.location && (
                            <div className="text-slate-400 flex items-center gap-1 truncate">
                              <MapPin size={10} />
                              {typeof session.location === 'string' 
                                ? session.location 
                                : session.location.building_name || session.location.organization || session.location.city || 'Location'}
                            </div>
                          )}
                        </div>
                      ))}
                      {sessions.length > 3 && (
                        <div className="text-teal-400 text-xs text-center">
                          +{sessions.length - 3} {translate('more', 'more')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Quick Stats */}
          <div className="mt-4 pt-4 border-t border-teal-700/30 flex items-center justify-between text-sm">
            <div className="flex items-center gap-4">
              <div className="text-teal-300">
                <span className="font-bold text-teal-200">{trainingSessions.length}</span> {translate('totalSessions', 'total sessions')}
              </div>
              <div className="text-blue-300">
                <span className="font-bold text-blue-200">
                  {weekDates.reduce((acc, date) => acc + getSessionsForDate(date).length, 0)}
                </span> {translate('thisWeek', 'this week')}
              </div>
            </div>
            {trainingSessions.length === 0 && (
              <span className="text-slate-400 text-xs">
                {translate('noTrainingSessionsYet', 'No training sessions scheduled yet')}
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Sessions Widget - Shows only when a day with sessions is clicked */}
      {selectedCalendarDate && (
        <Card className="bg-gradient-to-br from-indigo-900/40 to-purple-900/40 border-indigo-700/30 backdrop-blur-sm">
          <CardHeader className="pb-3">
            <CardTitle className="text-indigo-200 flex items-center justify-between text-lg">
              <div className="flex items-center">
                <Bell className="mr-2 text-indigo-400" size={20} />
                {translate('sessionsFor', 'Sessions for')} {selectedCalendarDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                <Badge className="ml-3 bg-indigo-600 text-white">{getSessionsForDate(selectedCalendarDate).length}</Badge>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelectedCalendarDate(null)}
                className="text-indigo-300 hover:text-white hover:bg-indigo-700/50"
              >
                ✕
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {getSessionsForDate(selectedCalendarDate).map((session) => (
                <div 
                  key={session.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-indigo-800/30 border border-indigo-700/30"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center bg-indigo-600">
                      <Calendar className="text-white" size={18} />
                    </div>
                    <div>
                      <p className="text-white font-medium">{session.name}</p>
                      <div className="flex items-center gap-3 text-xs">
                        {session.start_time && (
                          <span className="text-indigo-300 flex items-center">
                            <Clock size={12} className="mr-1" />
                            {session.start_time} - {session.end_time}
                          </span>
                        )}
                        {session.location && (
                          <span className="text-indigo-300 flex items-center">
                            <MapPin size={12} className="mr-1" />
                            {typeof session.location === 'string' 
                              ? session.location 
                              : session.location.building_name || session.location.organization || session.location.city || 'Location'}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Badge className="bg-indigo-600 text-white">
                    {session.discipline || 'Training'}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Capacity Alerts Widget */}
      {capacityAlerts.length > 0 && (
        <Card className="bg-gradient-to-br from-orange-900/40 to-red-900/40 border-orange-700/30 backdrop-blur-sm" data-testid="capacity-alerts-widget">
          <CardHeader className="pb-3">
            <CardTitle className="text-orange-200 flex items-center text-lg">
              <Users className="mr-2 text-orange-400" size={20} />
              {translate('capacityAlerts', 'Capacity Alerts')}
              {capacityAlerts.some(s => s.status === 'full') && (
                <Badge className="ml-3 bg-red-600 text-white animate-pulse">
                  {capacityAlerts.filter(s => s.status === 'full').length} {translate('full', 'Full')}
                </Badge>
              )}
              {capacityAlerts.some(s => s.status === 'nearFull') && (
                <Badge className="ml-2 bg-orange-600 text-white">
                  {capacityAlerts.filter(s => s.status === 'nearFull').length} {translate('nearFull', 'Near Full')}
                </Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {capacityAlerts.map((session) => {
                const currentParticipants = session.participants?.length || 0;
                const maxParticipants = session.pool_specs?.max_participants || 0;
                
                return (
                  <div 
                    key={session.id}
                    className={`flex items-center justify-between p-3 rounded-lg ${
                      session.status === 'full' 
                        ? 'bg-red-900/40 border border-red-600/50' 
                        : 'bg-orange-900/30 border border-orange-600/40'
                    }`}
                    data-testid={`capacity-alert-${session.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        session.status === 'full' ? 'bg-red-600' : 'bg-orange-600'
                      }`}>
                        <Users className="text-white" size={18} />
                      </div>
                      <div>
                        <p className="text-white font-medium">{session.name}</p>
                        <div className="flex items-center gap-3 text-xs">
                          <span className="text-orange-300 flex items-center">
                            <Calendar size={12} className="mr-1" />
                            {session.sessionDate.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                          </span>
                          <span className={`flex items-center font-semibold ${
                            session.status === 'full' ? 'text-red-300' : 'text-orange-300'
                          }`}>
                            <Users size={12} className="mr-1" />
                            {currentParticipants}/{maxParticipants}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`${
                        session.status === 'full' 
                          ? 'bg-red-600 text-white' 
                          : 'bg-orange-600 text-white'
                      }`}>
                        {session.percentage}%
                      </Badge>
                      <span className={`text-xs ${
                        session.status === 'full' ? 'text-red-400' : 'text-orange-400'
                      }`}>
                        {session.label}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-orange-400 text-xs mt-3 flex items-center">
              <AlertTriangle size={12} className="mr-1" />
              {translate('capacityAlertNote', 'Sessions at 80%+ capacity are shown above')}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Hero Image Section with Features */}
      <Card className="bg-gradient-to-r from-blue-900/30 to-teal-800/30 border-blue-700/20 backdrop-blur-sm overflow-hidden">
        <div className="flex flex-col lg:flex-row">
          {/* Image Section - Reduced size (25% width) */}
          <div className="relative h-48 lg:h-auto lg:w-1/4">
            <img 
              src="https://images.unsplash.com/photo-1548065822-2cd6b99550f8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwzfHx1bmRlcndhdGVyJTIwZGl2aW5nfGVufDB8fHx8MTc1NTAyOTQ5NXww&ixlib=rb-4.1.0&q=85"
              alt="Freediver underwater"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-transparent to-transparent"></div>
            <div className="absolute bottom-3 left-3 right-3">
              <h3 className="text-sm font-bold text-white mb-1 leading-tight">There is only one way to fail and that is to give up before you succeed.</h3>
            </div>
          </div>
          
          {/* Features Section - Now takes 75% width */}
          <div className="lg:w-3/4 p-6 flex flex-col justify-center">
            <h3 className="text-2xl font-bold text-teal-200 mb-4">{translate('welcomeTitle', 'Welcome to Apnea Coach Manager')}</h3>
            <p className="text-blue-200 mb-4 leading-relaxed">
              {translate('welcomeDescription', 'Your comprehensive platform for managing freediving competitions, tracking athlete performance, and building training programs.')}
            </p>
            <ul className="space-y-3 text-blue-300">
              <li className="flex items-start">
                <span className="text-teal-400 mr-2">✓</span>
                <span><strong className="text-white">{translate('competitionManagement', 'Competition Management')}</strong> — {translate('competitionManagementDesc', 'Create and manage competitions with multiple event types, real-time scoring, and live event monitoring.')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-400 mr-2">✓</span>
                <span><strong className="text-white">{translate('athleteTracking', 'Athlete Tracking')}</strong> — {translate('athleteTrackingDesc', 'Register freedivers, track their certifications, and monitor their performance history over time.')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-400 mr-2">✓</span>
                <span><strong className="text-white">{translate('performanceFollowUp', 'Performance Follow-Up')}</strong> — {translate('performanceFollowUpDesc', 'Record training performances with detailed metrics, coach validation, and automatic ranking.')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-400 mr-2">✓</span>
                <span><strong className="text-white">{translate('trainingSessions', 'Training Sessions')}</strong> — {translate('trainingSessionsDesc', 'Build custom training programs with exercises, templates, and session planning tools.')}</span>
              </li>
              <li className="flex items-start">
                <span className="text-teal-400 mr-2">✓</span>
                <span><strong className="text-white">{translate('liveEvents', 'Live Events')}</strong> — {translate('liveEventsDesc', 'Real-time judge interface with countdown timers, RP entry, and automatic result calculation.')}</span>
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Event Types Overview */}
      <div>
        <h3 className="text-2xl font-bold mb-6 text-blue-200">{translate('supportedEvents', 'Supported Events')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {eventTypes.map((eventType) => {
            const disciplineInfo = getDisciplineType(eventType.code);

            return (
              <Card key={eventType.id} className="bg-gradient-to-br from-blue-800/40 to-teal-800/40 border-blue-700/30 backdrop-blur-sm hover:scale-105 transition-transform duration-300 overflow-hidden">
                <div className="relative h-32">
                  <img 
                    src={eventImages[eventType.code]} 
                    alt={eventType.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 via-transparent to-transparent"></div>
                  <div className="absolute top-2 left-2 flex space-x-2">
                    <Badge variant="secondary" className="bg-blue-700/80 text-blue-200 border-blue-600/50">
                      {eventType.code}
                    </Badge>
                    <Badge variant="secondary" className={disciplineInfo.color}>
                      {disciplineInfo.type}
                    </Badge>
                  </div>
                </div>
                <CardHeader className="pb-3">
                  <CardTitle className="text-blue-200 text-lg">{getEventName(eventType.code, eventType.name)}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-blue-300 text-sm mb-3 leading-relaxed">
                    {getDetailedDescription(eventType.code, eventType)}
                  </p>
                  <Badge variant="secondary" className="bg-teal-700/50 text-teal-200 border-teal-600/50">
                    {getScoringRule(eventType.code)}
                  </Badge>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>

      {/* App Version Footer */}
      <div className="pt-12 pb-4 text-center" data-testid="app-version-footer">
        <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-blue-900/30 border border-blue-700/30 backdrop-blur-sm flex-wrap justify-center">
          <span className="text-blue-300 text-xs font-mono tracking-wider" data-testid="app-version-label">
            Apnea Coach Manager
          </span>
          <span className="w-1 h-1 rounded-full bg-blue-500"></span>
          <span className="text-cyan-300 text-xs font-mono font-semibold" data-testid="app-version-number">
            {APP_VERSION_LABEL}
          </span>
          <span className="w-1 h-1 rounded-full bg-blue-500"></span>
          <span className="text-blue-400 text-xs italic" data-testid="app-version-date">
            {APP_VERSION_NAME} • {APP_VERSION_DATE}
          </span>
          <span className="w-1 h-1 rounded-full bg-blue-500"></span>
          <span className="text-teal-300 text-xs" data-testid="app-current-date">
            {formatFrenchDate(new Date())}
          </span>
        </div>
      </div>
    </div>
  );
};

export default DashboardTab;
